/**
 * Displays information regarding the certificates ( labs includes, duration, description, etc)
 * when the user plan is free click on th 'buy' button to go to checkout area -> checkout session (stripe) -> subscribe to the Cert.
 * when the user plan is enterprise the 'buy' button changes to 'enroll' button and the cert is allocated to the user (no payment)
 */
import Styles from './LPathInfo.module.scss';
import { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Module imports
import axios from 'axios';
import { Typography, Drawer, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast, ToastContainer } from 'react-toastify';
import { Tooltip as ReactTooltip } from "react-tooltip";

// Components import.
import { useAPI, AuthContext, useCan } from 'components/lib';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Checkout } from 'components/global/Checkout/Checkout';
import { Notification } from 'components/notifications/notification';
import { CalendarTodayOutlined, HourglassBottomOutlined, InfoOutlined, MenuBookOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';

export function LPathInfo(props) {

    const [lPathList, setLPathList] = useState([]);
    const [showLabDescr, setShowLabDescr] = useState();
    const [showCheckOut, setShowCheckout] = useState(false);
    const [enrolledLabs, setEnrolledLabs] = useState(0)
    const [calcPrices, setCalcPrices] = useState({ discount: '', strikedTotal: '', subTotal: '', currencySymbol: '', interval: '' });
    const [buyLoading, setBuyLoading] = useState(false);

    const authContext = useContext(AuthContext);
    const currencySymbol = authContext?.user?.currencySymbol;

    const navigate = useNavigate();
    // eslint-disable-next-line
    const { lpath_id } = useParams();

    const { data, loading } = useAPI(`/api/lpath/${lpath_id}/labs`, 'GET');
    const isScholar = useCan('user.months.view');

    //receiving the cert data
    useEffect(() => {
        if (data) {
            setLPathList(data);
        }
    }, [data])

    const selectedLPath = useMemo(() => {
        if (!lPathList?.labs) return new Set();

        return new Set(
            lPathList.labs
                .filter(lab => !lab.isEnrolled)
                .map(lab => lab.id)
        );
    }, [lPathList?.labs]);

    useEffect(() => {
        const pricing = lPathList?.certs?.pricing;
        const labs = lPathList?.labs || [];

        if (!pricing) return;

        // enrolled labs count
        const enrolledLabList = labs.filter(lab => lab.isEnrolled);
        const enrolledCount = enrolledLabList.length;
        setEnrolledLabs(enrolledCount);

        const actualPrice = pricing.actual_price ?? 0;
        const price = pricing.price ?? 0;
        const basePrice = pricing.meta_data?.base_price ?? 0;

        const labDiscount = enrolledCount * basePrice;
        const productDiscount = actualPrice - price;

        const discount = Math.max(productDiscount + labDiscount, 0);
        const subTotal = Math.max(price - labDiscount, 0);

        setCalcPrices({
            currencySymbol,
            discount,
            strikedTotal: actualPrice,
            subTotal,
            interval: pricing.interval
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lPathList?.certs, lPathList?.labs])

    const handleEnroll = (isTrial) => {
        const data = {
            certId: lpath_id
        }
        setBuyLoading(true)
        const options = {
            method: 'POST',
            url: `/api/lpath/enroll`,
            params: {
                isTrial: isTrial ? 1 : 0,
            },
            headers: {
                accept: 'application/json',
            },
            data: data,
        };

        axios
            .request(options)
            .then(function (response) {
                let status = response.status;
                if (status === 201) {
                    toast.dark(props.t('LearningPath.LPathInfo.toast.success'), {
                        type: 'success',
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    navigate('/lpaths')
                } else {
                    toast.dark(props.t('LearningPath.LPathInfo.toast.failed'), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });

                }
            })
            .catch((err) => {
                let status = err.response.status;
                if (status === 406) {
                    toast.dark((props.t('LearningPath.LPathInfo.toast.limit'), { enrollLimit: process.env.REACT_APP_ENROLL_LABS_LIMIT }), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                } else {
                    toast.dark(props.t('LearningPath.LPathInfo.toast.error'), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }

            })
            .finally(() => setBuyLoading(false));
    }

    function getCertDuration(labs) {
        const count = labs?.reduce((acc, lab) => acc + (lab?.is_project ? lab?.nodeCount * 2 : lab?.nodeCount), 0)
        return count
    }

    return (
        <>
            <ToastContainer
                id="warning-toast"
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='cert' />
            <Typography
                variant="h6"
                sx={{ display: 'flex', gap: '16px', padding: '16px', mb: '-15px', color: 'rgb(0, 74, 173)' }}
            >
                <BreadCrumbs
                    onClickPaths={['/lpaths', '']}
                    crumbs={[
                        'Certifications',
                        props.t('LearningPath.LPathInfo.breadcrumbs.curr', { lpath: lPathList?.certs?.name })
                    ]}
                />
            </Typography>

            <div >
                <Drawer anchor='right' open={showCheckOut} onClose={() => { setShowCheckout(!showCheckOut) }}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'transparent',
                        }
                    }}
                >
                    <div className={Styles.checkoutDrawer}>
                        <Checkout
                            purchaseEndpoint='/api/lpath/purchase'
                            checkoutName='cert'
                            availableItems={[lPathList?.certs]}
                            individualPricing={false}
                            onClose={() => setShowCheckout(false)}
                            enrolledLabs={enrolledLabs}
                            userSelItems={selectedLPath}
                        />
                    </div>
                </Drawer>
            </div>

            {(loading || buyLoading) ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : (
                <div className={Styles.CardContainer}>
                    <div className={Styles.certDetails}>
                        <div className={Styles.imageContainer} >
                            <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${lPathList?.certs?.icon}`} className={Styles.courseImage} alt="certificate" />
                        </div>
                        <p className={Styles.certName}>{lPathList?.certs?.name}</p>
                        <p className={Styles.certDescr}>{lPathList?.certs?.descr}</p>
                        <div className={Styles.pricingDesign}>
                            <Typography variant='h5' sx={{ fontWeight: 600 }}>
                                Get Started Today
                            </Typography>
                            {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!lPathList?.certs?.org_purchase || lPathList?.certs?.status !== 'active'))) && (
                                <div style={{ marginTop: '0.5em', display: 'flex', gap: '1em', alignItems: 'center', padding: '0.5em', width: '100%' }}>
                                    <Typography variant='h6' sx={{ textDecoration: 'line-through', color: 'darkgray' }}>{calcPrices.currencySymbol} {calcPrices.strikedTotal}</Typography>
                                    <Typography variant='h4' sx={{ fontWeight: 600 }}> {calcPrices.currencySymbol} {calcPrices.subTotal}</Typography>
                                    <span id='itemSaving' className={Styles.itemSaving}>Save {calcPrices.currencySymbol} {calcPrices.discount}</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '1em', width: '100%', alignItems: 'center', marginTop: '1em' }}>
                                {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!lPathList?.certs?.org_purchase || lPathList?.certs?.status !== 'active'))) ? (
                                    <>
                                        <button className={Styles.purchaseBtn} onClick={() => { setShowCheckout(!showCheckOut) }}>
                                            Buy now
                                        </button>
                                        {!['trial', 'trial_expired', 'expired', 'disabled'].includes(lPathList?.certs?.subscriptionStatus) && (
                                            <button className={Styles.trialBtn} onClick={() => { handleEnroll(true) }}>
                                                Free Trial
                                                <Tooltip title='The first module of each course can be tried for free' arrow placement='top'>
                                                    <InfoOutlined fontSize='small' sx={{ ml: '0.5em' }} />
                                                </Tooltip>
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <button className={Styles.enrollBtn} onClick={() => { handleEnroll(false) }}>
                                        Enroll Now
                                    </button>
                                )}

                            </div>
                        </div>
                        <div style={{ fontSize: "1em", color: '#1450a0', marginTop: '1.5em', display: 'flex', gap: '1.5em', alignItems: 'center' }}>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><MenuBookOutlined fontSize='small' /> {lPathList?.labs?.length} Courses </span>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><HourglassBottomOutlined fontSize='small' /> {getCertDuration(lPathList?.labs)} Hours </span>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><WorkspacePremiumOutlined fontSize='small' /> NASSCOM Certificate</span>
                            {isScholar && (
                                <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><CalendarTodayOutlined fontSize='small' /> {calcPrices?.interval} Months Access</span>
                            )}
                        </div>
                    </div>

                    <div className={Styles.labsInclude}>
                        <p style={{ fontSize: "30px", fontWeight: 700, color: 'black' }}>{props.t('LearningPath.LPathInfo.labs.include')}</p>
                        <p style={{ fontSize: '14px', color: "grey", marginTop: '-10px' }}>{props.t('LearningPath.LPathInfo.labs.overview')}</p>
                        <div style={{ width: "100%", backgroundColor: '#E5E7E8', height: '1.5px', marginTop: '15px', marginBottom: '15px' }} />
                        <div className={Styles.labListContainer}>
                            {
                                lPathList?.labs?.map((lab, index) => {
                                    return (
                                        <div
                                            className={Styles.labList}
                                            key={lab.id + index}
                                            data-tooltip-id={((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!lPathList?.org_purchase || lPathList?.status !== 'active'))) && lab.isEnrolled) && "my-tooltip-1"}
                                            style={{
                                                height: 'fit-content',
                                                backgroundColor: ((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!lPathList?.org_purchase || lPathList?.status !== 'active'))) && lab.isEnrolled) ? "	#D3D3D3" : "white",
                                                filter: ((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!lPathList?.org_purchase || lPathList?.status !== 'active'))) && lab.isEnrolled) ? "saturate(0)" : "none",
                                            }}
                                        >
                                            <div style={{ display: 'flex' }}>
                                                <img className={Styles.labsImg} src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${lab.icon}`} alt={props.t('LearningPath.LPathInfo.altIcon')} />
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        fontSize: '18px', color: 'black',
                                                        fontWeight: 'bold',
                                                        padding: '10px',
                                                        width: '100%',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span>
                                                        <p>{lab.name}</p>
                                                    </span>
                                                    <button style={{ height: '100%' }} onClick={() => { showLabDescr !== lab.id ? setShowLabDescr(lab.id) : setShowLabDescr(null) }} ><KeyboardArrowDownIcon /></button>
                                                </span>
                                            </div>
                                            <div className={Styles.labsDescr} hidden={showLabDescr !== lab.id}>
                                                {lab.descr}
                                            </div>

                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                </div>
            )}
            <ReactTooltip
                id="my-tooltip-1"
                place="left"
                content={props.t('LearningPath.LPathInfo.message')}
            // isOpen={couponAlert}
            />
        </>
    );
}